import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { projects } from "./data/projectsData";
import Navbar from "./components/navbar";
import Footer from "./components/footer";
import FontLoader from "./components/fontLoader";
import { PageMeta } from "../seo/PageMeta";

import nosProjets from "../images/nosProjets.png";

const pageGradient =
  "linear-gradient(180deg, #F8FBFF 0%, #E9F5FF 50%, #DCEEFF 100%)";

const contentEase = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: contentEase, delay },
  }),
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(media.matches);

    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

function ProjectCard({ project, index, hoveredIndex, onHoverStart, onHoverEnd, isMobile }) {
  const isHovered = hoveredIndex === index;
  const showInfo = isMobile || isHovered;

  const card = (
    <motion.article
      className="relative h-[min(480px,70vh)] w-[min(320px,82vw)] shrink-0 snap-center overflow-hidden rounded-[2rem] border border-[#0C1925]/10 shadow-[0_20px_50px_rgba(12,25,37,0.14)] md:h-[min(520px,72vh)] md:w-[min(340px,38vw)]"
      onMouseEnter={() => onHoverStart(index)}
      onMouseLeave={onHoverEnd}
    >
      <img
        src={project.image}
        alt={project.title}
        className="absolute inset-0 h-full w-full object-cover"
      />

      <motion.div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0C1925]/55 via-[#0C1925]/25 to-[#0C1925]/65"
        initial={false}
        animate={{ opacity: showInfo ? 1 : 0 }}
        transition={{ duration: 0.3, ease: contentEase }}
      />

      <div className="relative flex h-full flex-col justify-between p-6 md:p-8">
        <div className="text-left">
          <motion.h2
            className="text-2xl font-semibold tracking-tight text-white md:text-3xl"
            style={{ fontFamily: "'Inter', sans-serif" }}
            initial={false}
            animate={
              isMobile
                ? { opacity: 1, y: 0, fontSize: "1.875rem" }
                : isHovered
                  ? { opacity: 1, y: 0, fontSize: "2rem" }
                  : { opacity: 0, y: 12, fontSize: "1.75rem" }
            }
            transition={{ duration: 1, ease: contentEase }}
          >
            {project.title}
          </motion.h2>

          <motion.p
            className="mt-3 max-w-[28ch] text-sm leading-relaxed text-white/80 md:text-base"
            style={{ fontFamily: "'Inter', sans-serif" }}
            initial={false}
            animate={
              isMobile
                ? { opacity: 1, x: 0 }
                : isHovered
                  ? { opacity: 1, x: 0 }
                  : { opacity: 0, x: -40 }
            }
            transition={{ duration: 1.2, ease: contentEase }}
          >
            {project.description}
          </motion.p>
        </div>

        <motion.div
          className="flex items-end justify-between gap-4"
          initial={false}
          animate={
            isMobile
              ? { opacity: 1, y: 0 }
              : isHovered
                ? { opacity: 1, y: 0 }
                : { opacity: 0, y: 16 }
          }
          transition={{ duration: 1, ease: contentEase, delay: isHovered ? 0.08 : 0 }}
        >
          <div className="flex flex-wrap gap-2">
            {project.services?.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-medium tracking-wide text-white backdrop-blur-sm"
              >
                {tag}
              </span>
            ))}
          </div>

          {project.url ? (
            <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/35 bg-white/10 px-4 py-2 text-sm font-medium tracking-wide text-white backdrop-blur-sm">
              Visiter
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M7 17L17 7" />
                <path d="M7 7h10v10" />
              </svg>
            </span>
          ) : null}
        </motion.div>
      </div>
    </motion.article>
  );

  if (!project.url) return card;

  return (
    <a
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Visiter le site de ${project.title}`}
      className="block shrink-0 snap-center rounded-[2rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4F9ECF]"
    >
      {card}
    </a>
  );
}

function Projet() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const isMobile = useIsMobile();

  return (
    <>
      <PageMeta
        title="Nos projets"
        description="Découvrez les sites web et solutions numériques réalisés par LMS Technologies pour nos clients au Québec."
        path="/projet"
      />
      <FontLoader />
      <Navbar />
      <main style={{ fontFamily: "'Inter', sans-serif" }}>
        <section
          className="min-h-screen pt-32 pb-24 px-6 md:pt-36"
          style={{ background: pageGradient }}
        >
          <div className="mx-auto flex w-full max-w-7xl flex-col items-center">
            <motion.header
              className="flex w-full flex-col items-center text-center"
              initial="hidden"
              animate="visible"
            >
              <motion.div
                variants={fadeUp}
                custom={0}
                className="flex justify-center [&_img]:h-auto [&_img]:max-w-[min(260px,88vw)] [&_img]:w-full [&_img]:object-contain md:[&_img]:max-w-[400px] md:[&_img]:w-auto"
              >
                <img src={nosProjets} alt="Nos projets" width={400} height={400} />
              </motion.div>
              <motion.p
                variants={fadeUp}
                custom={0.1}
                className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#1a1a2e]/60 md:text-base"
              >
                Voici les projets que nous avons réalisés pour nos clients — des
                solutions concrètes, pensées pour performer.
              </motion.p>
            </motion.header>

            <motion.div
              variants={fadeUp}
              custom={0.4}
              initial="hidden"
              animate="visible"
              className="mt-14 flex w-full justify-center md:mt-20"
            >
              <div className="flex w-full snap-x snap-mandatory justify-center gap-6 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] md:w-auto md:flex-wrap md:overflow-visible md:pb-0 [&::-webkit-scrollbar]:hidden">
                {projects.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    index={index}
                    hoveredIndex={hoveredIndex}
                    onHoverStart={isMobile ? () => {} : setHoveredIndex}
                    onHoverEnd={isMobile ? () => {} : () => setHoveredIndex(null)}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default Projet;
